import {create} from 'zustand'

/**
 * Dot severities, in priority order. The toggle shows a single coloured dot reflecting the
 * most important un-acknowledged export state since the sidebar was last opened:
 * `failed` (red) > `pending` (orange, also covers `processing`) > `ready` (green).
 */
export type ExportIndicatorSeverity = 'failed' | 'pending' | 'ready'

const SEVERITY_PRIORITY: Record<ExportIndicatorSeverity, number> = {
    failed: 3,
    pending: 2,
    ready: 1
}

/** Map a backend job status to a dot severity, or null for states that warrant no dot. */
const statusToSeverity = (status: string): ExportIndicatorSeverity | null => {
    switch (status) {
        case 'pending':
        case 'processing':
            return 'pending'
        case 'ready':
            return 'ready'
        case 'failed':
            return 'failed'
        default:
            // cancelled / archived / expired → no dot
            return null
    }
}

/**
 * Tracks the dot-relevant status of each export job seen since the sidebar was last opened,
 * driving the coloured dot on the toggle (FR-002b, extended with per-state colours). Fed by the
 * socket listener (every status change) and by the enqueue flow.
 *
 * A per-job map (rather than a single flag) keeps transitions correct: a job going
 * `pending → ready` updates its own entry, so the dot turns green instead of staying orange.
 *
 * Opening the sidebar `acknowledge`s the *finished* results (ready/failed), but leaves
 * still-in-progress (pending) jobs in place: an in-flight export is not "seen" just because the
 * drawer was opened, so its orange dot persists until the job actually finishes.
 */
interface ExportsIndicatorState {
    /** jobId → its current dot severity. */
    jobs: Record<string, ExportIndicatorSeverity>
    track: (id: string, status: string) => void
    acknowledge: () => void
}

export const useExportsIndicatorStore = create<ExportsIndicatorState>(set => ({
    jobs: {},
    track: (id, status) =>
        set(state => {
            const severity = statusToSeverity(status)
            const next = {...state.jobs}
            if (severity) {
                next[id] = severity
            } else {
                delete next[id]
            }
            return {jobs: next}
        }),
    acknowledge: () =>
        set(state => ({
            jobs: Object.fromEntries(Object.entries(state.jobs).filter(([, severity]) => severity === 'pending'))
        }))
}))

/** Highest-priority severity across all tracked jobs (failed > pending > ready), or null when none. */
export const selectIndicatorSeverity = (state: ExportsIndicatorState): ExportIndicatorSeverity | null => {
    let top: ExportIndicatorSeverity | null = null
    for (const severity of Object.values(state.jobs)) {
        if (!top || SEVERITY_PRIORITY[severity] > SEVERITY_PRIORITY[top]) {
            top = severity
        }
    }
    return top
}
